import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { displayDeliveryShipping } from '../../util/configHelpers';

/**
 * Component to display shipping policy information including shipping time,
 * cancellations, delivery regions, returns/exchanges, and purchase protection
 */
const SectionShippingPolicyMaybe = props => {
  const { publicData, listingTypeConfig, intl } = props;

  if (!publicData) {
    return null;
  }

  const { 
    shippingEnabled, 
    shippingTime, 
    orderCancellations, 
    deliversTo, 
    returnsExchanges 
  } = publicData;
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  
  // Only show if shipping is enabled and at least one shipping field is available
  if (!displayShipping || !shippingEnabled || (!shippingTime && !orderCancellations && !deliversTo && !returnsExchanges)) {
    return null;
  }

  // Helper functions to get human-readable values
  const getShippingTimeMessage = (shippingTime) => {
    switch (shippingTime) {
      case '1-3-days':
        return intl.formatMessage({ id: 'SectionShippingPolicy.duration1To3Days' });
      case '1-2-weeks':
        return intl.formatMessage({ id: 'SectionShippingPolicy.duration1To2Weeks' });
      case '4-weeks':
        return intl.formatMessage({ id: 'SectionShippingPolicy.duration4Weeks' });
      default:
        return shippingTime;
    }
  };

  const getOrderCancellationMessage = (orderCancellations) => {
    switch (orderCancellations) {
      case 'not-accepted':
        return intl.formatMessage({ id: 'SectionShippingPolicy.orderCancellationsNotAccepted' });
      case 'within-4-hours':
        return intl.formatMessage({ id: 'SectionShippingPolicy.orderCancellationsWithin4Hours' });
      case 'within-24-hours':
        return intl.formatMessage({ id: 'SectionShippingPolicy.orderCancellationsWithin24Hours' });
      default:
        return orderCancellations;
    }
  };

  const getDeliversToMessage = (deliversTo) => {
    if (!deliversTo || deliversTo.length === 0) return null;
    
    const regionMessages = deliversTo.map(region => {
      switch (region) {
        case 'worldwide':
          return intl.formatMessage({ id: 'SectionShippingPolicy.deliversToWorldwide' });
        case 'usa':
          return intl.formatMessage({ id: 'SectionShippingPolicy.deliversToUSA' });
        case 'israel':
          return intl.formatMessage({ id: 'SectionShippingPolicy.deliversToIsrael' });
        case 'canada':
          return intl.formatMessage({ id: 'SectionShippingPolicy.deliversToCanada' });
        case 'uk':
          return intl.formatMessage({ id: 'SectionShippingPolicy.deliversToUK' });
        default:
          return region;
      }
    });
    
    return regionMessages.join(', ');
  };

  const getReturnsExchangesMessage = (returnsExchanges) => {
    switch (returnsExchanges) {
      case 'not-accepted':
        return intl.formatMessage({ id: 'SectionShippingPolicy.returnsExchangesNotAccepted' });
      case 'within-14-days':
        return intl.formatMessage({ id: 'SectionShippingPolicy.returnsExchangesWithin14Days' });
      case 'within-21-days':
        return intl.formatMessage({ id: 'SectionShippingPolicy.returnsExchangesWithin21Days' });
      case 'within-14-days-fee':
        return intl.formatMessage({ id: 'SectionShippingPolicy.returnsExchangesWithin14DaysFee' });
      case 'within-21-days-fee':
        return intl.formatMessage({ id: 'SectionShippingPolicy.returnsExchangesWithin21DaysFee' });
      default:
        return returnsExchanges;
    }
  };

  return (
    <section className="p-4 md:p-0 mb-12 pt-0 md:pt-2 lg:mb-10 lg:pt-2 scroll-mt-24 lg:scroll-mt-20">
      <h2 className="font-semibold text-lg leading-6 text-gray-900 mb-4 md:mb-6 md:text-xl md:leading-7">
        <FormattedMessage id="SectionShippingPolicy.title" />
      </h2>
      
      <ul className="pb-1 m-0">
        {shippingTime && (
          <li className="inline-flex justify-between w-full list-none font-normal leading-6 py-2 gap-2 border-b border-gray-100 last:border-0 md:py-1 md:leading-8">
            <span className="font-semibold tracking-tight">
              <FormattedMessage id="SectionShippingPolicy.shippingTimeLabel" />
            </span>
            <span>
              <FormattedMessage 
                id="SectionShippingPolicy.message"
                values={{ duration: getShippingTimeMessage(shippingTime) }}
              />
            </span>
          </li>
        )}
        
        {orderCancellations && (
          <li className="inline-flex justify-between w-full list-none font-normal leading-6 py-2 gap-2 border-b border-gray-100 last:border-0 md:py-1 md:leading-8">
            <span className="font-semibold tracking-tight">
              <FormattedMessage id="SectionShippingPolicy.orderCancellationsLabel" />
            </span>
            <span>{getOrderCancellationMessage(orderCancellations)}</span>
          </li>
        )}
        
        {deliversTo && deliversTo.length > 0 && (
          <li className="inline-flex justify-between w-full list-none font-normal leading-6 py-2 gap-2 border-b border-gray-100 last:border-0 md:py-1 md:leading-8">
            <span className="font-semibold tracking-tight">
              <FormattedMessage id="SectionShippingPolicy.deliversToLabel" />
            </span>
            <span>{getDeliversToMessage(deliversTo)}</span>
          </li>
        )}
        
        {returnsExchanges && (
          <li className="inline-flex justify-between w-full list-none font-normal leading-6 py-2 gap-2 border-b border-gray-100 last:border-0 md:py-1 md:leading-8">
            <span className="font-semibold tracking-tight">
              <FormattedMessage id="SectionShippingPolicy.returnsExchangesLabel" />
            </span>
            <span>{getReturnsExchangesMessage(returnsExchanges)}</span>
          </li>
        )}
      </ul>

      {/* Joodaic Purchase Protection Section */}
      <div className="bg-gray-100 border border-gray-200 py-3 px-4 rounded-lg mt-8">
        <div className="my-0 mb-3 text-lg font-semibold text-gray-700 leading-tight text-md">
          <FormattedMessage id="SectionShippingPolicy.purchaseProtectionTitle" />
        </div>
        <div className="my-0 mb-3 text-lg font-normal text-gray-700 leading-tight text-sm">
          <FormattedMessage id="SectionShippingPolicy.purchaseProtectionDescription" />
          <a href="/p/disputes-policy" className="ml-2 text-sm no-underline font-medium hover:underline hover:text-gray-700">
          <FormattedMessage id="SectionShippingPolicy.viewMoreDetails" />
        </a>
        </div>
      </div>
    </section>
  );
};

export default SectionShippingPolicyMaybe; 